import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSignUp, useUser, useAuth as useClerkAuth } from '@clerk/react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone, Loader2, Car, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, errors: clerkErrors, fetchStatus } = useSignUp();
  const { isLoaded: userLoaded, isSignedIn } = useUser();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: searchParams.get('role') || 'customer'
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState('signup'); // 'signup' or 'verification'
  const [resendTimer, setResendTimer] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn && user && user.role !== 'pending') {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'business') {
        navigate('/dashboard/overview');
      } else {
        navigate('/dashboard');
      }
    } else if (isSignedIn && user?.role === 'pending') {
      navigate('/select-role');
    }
  }, [isSignedIn, user, navigate]);

  // Check if we need to show verification step based on signUp status
  useEffect(() => {
    if (signUp?.status === 'missing_requirements' && 
        signUp?.unverifiedFields?.includes('email_address') &&
        signUp?.missingFields?.length === 0) {
      setStep('verification');
    }
  }, [signUp?.status, signUp?.unverifiedFields, signUp?.missingFields]);

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    
    if (!signUp) {
      toast.error('Sign up not ready. Please refresh the page.');
      return;
    }
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!agreedToTerms || !agreedToPrivacy) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);
    try {
      // Create the user with Clerk (v6 API)
      const { error } = await signUp.create({
        emailAddress: formData.email,
        password: formData.password,
        firstName: formData.name.split(' ')[0],
        lastName: formData.name.split(' ').slice(1).join(' ') || undefined,
      });

      if (error) {
        console.error('Sign up create error:', error);
        toast.error(error.message || 'Registration failed');
        setLoading(false);
        return;
      }

      // Check if we need to verify email
      if (signUp.status === 'missing_requirements' && 
          signUp.unverifiedFields?.includes('email_address')) {
        // Send email verification code (v6 API)
        await signUp.verifications.sendEmailCode();
        setStep('verification');
        setResendTimer(60);
        toast.info('Verification code sent to your email');
      } else if (signUp.status === 'complete') {
        // Account created without email verification
        sessionStorage.setItem('pendingRole', formData.role);
        sessionStorage.setItem('pendingPhone', formData.phone || '');
        await signUp.finalize({
          navigate: () => {
            navigate('/select-role');
          }
        });
      } else {
        console.log('Sign up status:', signUp.status, signUp);
        toast.info('Please check your email to continue');
      }
    } catch (error) {
      console.error('Sign up error:', error);
      const errorMessage = error.errors?.[0]?.longMessage || error.errors?.[0]?.message || error.message || 'Registration failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode) {
      toast.error('Please enter the verification code');
      return;
    }
    
    if (!signUp) {
      toast.error('Sign up session expired. Please try again.');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      // Verify email code (v6 API)
      await signUp.verifications.verifyEmailCode({
        code: verificationCode,
      });

      if (signUp.status === 'complete') {
        // Store the selected role in session storage for the SelectRolePage
        sessionStorage.setItem('pendingRole', formData.role);
        sessionStorage.setItem('pendingPhone', formData.phone || '');
        
        // Finalize sign up (v6 API)
        await signUp.finalize({
          navigate: () => {
            toast.success('Email verified! Please complete your registration.');
            navigate('/select-role');
          }
        });
      } else {
        console.log('Verification status:', signUp.status);
        toast.error('Verification incomplete. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.errors?.[0]?.message || error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || !signUp) return;
    
    setLoading(true);
    try {
      // Resend email code (v6 API)
      await signUp.verifications.sendEmailCode();
      setResendTimer(60);
      toast.success('Verification code resent');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // Store the selected role for after OAuth
    sessionStorage.setItem('pendingRole', formData.role);
    
    setGoogleLoading(true);
    try {
      if (!signUp) {
        throw new Error('Sign up not initialized');
      }
      
      // Use signUp.sso for OAuth in Clerk v6+
      const { error } = await signUp.sso({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/select-role`,
        redirectCallbackUrl: `${window.location.origin}/sso-callback`,
      });
      
      if (error) {
        console.error('Google sign-up error:', error);
        toast.error(error.message || 'Google sign-up failed');
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error('Google sign-up error:', error);
      toast.error(error.errors?.[0]?.message || error.message || 'Google sign-up failed');
      setGoogleLoading(false);
    }
  };

  if (!userLoaded) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-red-500 flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">
            {step === 'signup' ? 'Create an account' : 'Verify your email'}
          </h1>
          <p className="text-neutral-400 mt-2">
            {step === 'signup' 
              ? "Join R3vit's auto services marketplace" 
              : `Enter the code sent to ${formData.email}`
            }
          </p>
        </div>

        {step === 'signup' ? (
          <>
            {/* Google Sign-Up Button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={googleLoading}
                className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-full flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign up with Google
                  </>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0a0a0a] text-neutral-500">or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSignUpSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Doe"
                    className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white">Phone (optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(604) 555-0123"
                    className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="register-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-neutral-500">Must be at least 8 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              {/* Clerk CAPTCHA container - required for bot protection */}
              <div id="clerk-captcha" />

              {/* Terms and Privacy Policy Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-[#121212] text-red-500 focus:ring-red-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-neutral-400 group-hover:text-neutral-300">
                    I have read and agree to the{' '}
                    <Link to="/terms" target="_blank" className="text-red-400 hover:text-red-300 underline">
                      Terms of Service
                    </Link>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-[#121212] text-red-500 focus:ring-red-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-neutral-400 group-hover:text-neutral-300">
                    I have read and agree to the{' '}
                    <Link to="/privacy" target="_blank" className="text-red-400 hover:text-red-300 underline">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              </div>

              <Button 
                type="submit" 
                disabled={loading || fetchStatus === 'fetching' || !agreedToTerms || !agreedToPrivacy}
                className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit"
              >
                {loading || fetchStatus === 'fetching' ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
              </Button>

              {formData.role === 'business' && (
                <p className="text-xs text-neutral-500 text-center">
                  Subscription is $100/month after creating your business profile.
                </p>
              )}
            </form>
          </>
        ) : (
          /* Verification Code Form */
          <form onSubmit={handleVerificationSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-white">Verification Code</Label>
              <Input 
                id="code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                className="bg-[#121212] border-white/10 text-white h-12 text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading || !verificationCode}
              className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Email'}
            </Button>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep('signup');
                  setVerificationCode('');
                }}
                className="text-neutral-400 hover:text-white text-sm"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendTimer > 0}
                className="text-red-500 hover:text-red-400 text-sm disabled:text-neutral-600"
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-neutral-400 mt-8">
          Already have an account?{' '}
          <Link to="/login" className="text-red-500 hover:text-red-400">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
